import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import axios from 'axios';
import { 
  FiPlus, FiEdit2, FiTrash2, FiX, FiCheck,
  FiMapPin, FiPhone, FiHome
} from 'react-icons/fi';
import { API_BASE_URL } from '../../config';
import AdminLayout from './AdminLayout';

const Showrooms = () => {
  const navigate = useNavigate();
  const [showrooms, setShowrooms] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    phone: ''
  });

  const token = localStorage.getItem('token');

  const fetchShowrooms = useCallback(async () => {
    setLoading(true);
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${API_BASE_URL}/api/showrooms`, config);

      let showroomsData = [];
      if (Array.isArray(response.data)) {
        showroomsData = response.data;
      } else if (response.data.data && Array.isArray(response.data.data)) {
        showroomsData = response.data.data;
      } else if (response.data.showrooms && Array.isArray(response.data.showrooms)) {
        showroomsData = response.data.showrooms;
      }

      setShowrooms(showroomsData);
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('Failed to fetch showrooms');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    fetchShowrooms();
  }, [token, navigate, fetchShowrooms]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      if (editingItem) {
        await axios.put(
          `${API_BASE_URL}/api/showrooms/${editingItem.id}`,
          formData,
          config
        );
        toast.success('Showroom updated successfully');
      } else {
        await axios.post(`${API_BASE_URL}/api/showrooms`, formData, config);
        toast.success('Showroom added successfully');
      }
      
      setShowModal(false);
      setEditingItem(null);
      resetForm();
      fetchShowrooms();
    } catch (error) {
      console.error('Submit error:', error);
      toast.error(error.response?.data?.error || 'Operation failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this showroom?')) return;
    
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.delete(`${API_BASE_URL}/api/showrooms/${id}`, config);
      toast.success('Showroom deleted successfully');
      fetchShowrooms();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error.response?.data?.error || 'Delete failed');
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name || '',
      address: item.address || '',
      phone: item.phone || ''
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      address: '',
      phone: ''
    });
  };

  return (
    <AdminLayout>
      <div className="p-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">Showrooms</h1>
            <p className="text-gray-600 mt-1">Manage your showroom locations</p>
          </div>
          <button
            onClick={() => {
              setEditingItem(null);
              resetForm();
              setShowModal(true);
            }}
            className="bg-philips-blue text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-philips-dark-blue transition-colors"
          >
            <FiPlus /> Add Showroom
          </button>
        </div>

        {/* Content */}
        {loading ? (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-philips-blue"></div>
            <p className="mt-2 text-gray-600">Loading showrooms...</p>
          </div>
        ) : showrooms.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <FiMapPin className="mx-auto text-6xl text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Showrooms Yet</h3>
            <p className="text-gray-500 mb-4">Get started by adding your first showroom.</p>
            <button
              onClick={() => {
                setEditingItem(null);
                resetForm();
                setShowModal(true);
              }}
              className="bg-philips-blue text-white px-4 py-2 rounded-lg inline-flex items-center gap-2 hover:bg-philips-dark-blue"
            >
              <FiPlus /> Add Showroom
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {showrooms.map((showroom) => (
              <div key={showroom.id} className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow">
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="bg-philips-light-blue p-3 rounded-lg">
                      <FiHome className="text-philips-blue text-xl" />
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEdit(showroom)}
                        className="text-blue-600 hover:text-blue-800 p-1"
                        title="Edit"
                      >
                        <FiEdit2 />
                      </button>
                      <button
                        onClick={() => handleDelete(showroom.id)}
                        className="text-red-600 hover:text-red-800 p-1"
                        title="Delete"
                      >
                        <FiTrash2 />
                      </button>
                    </div>
                  </div>
                  
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">{showroom.name}</h3>
                  
                  {showroom.address && (
                    <p className="text-gray-600 text-sm mb-2 flex items-start gap-2">
                      <span className="text-gray-400">📍</span> {showroom.address}
                    </p>
                  )}
                  
                  {showroom.phone && (
                    <p className="text-gray-600 text-sm mb-3 flex items-center gap-2">
                      <FiPhone className="text-gray-400" /> {showroom.phone}
                    </p>
                  )}
                  
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <p className="text-sm text-gray-500">
                      Sales Persons: <span className="font-semibold text-gray-700">{showroom.sales_persons_count || 0}</span>
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">
                {editingItem ? 'Edit Showroom' : 'Add New Showroom'}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-gray-500 hover:text-gray-700">
                <FiX size={24} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Showroom Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="input-field"
                  required
                />
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
                <textarea
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="input-field"
                  rows="3"
                  placeholder="Enter complete address"
                />
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="input-field"
                  placeholder="Contact number"
                />
              </div>
              
              <div className="flex gap-3 mt-6">
                <button type="submit" className="btn-primary flex-1 flex items-center justify-center gap-2">
                  <FiCheck /> {editingItem ? 'Update' : 'Save'}
                </button>
                <button type="button" onClick={() => setShowModal(false)} className="btn-secondary flex-1">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default Showrooms;